import _ from 'lodash';
import moment from 'moment';
import constants from '$$helpers/constants';
import estimationsModule from '../../../components/estimations';
import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import timeTrackingModel from '$$timeTrackingModel';
import app from '$$app';
import reportHelpers from '../include/helpers';
import globalToast from '$$vueCmp/toast';
import { ColorCycler } from '../include/helpers';

const RESOURCE_TYPES = {
  TIME: 1,
  ITEM: 2,
  COST: 3,
};

class TimeByResourceReport {
  constructor() {
    this.timeWorker = null;
    this.workerData = {};
    this.tasksWithResourcesWithoutEstimation = {};
    this.calculationMode = null;
  }

  initTimeWorker() {
    if (window.Worker && !this.timeWorker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.timeWorker = new Worker('/workers/reports_time_worker.js' + `?${version}`);
    }

    this._initEvents();
  }

  _initEvents() {
    const that = this;

    this.timeWorker.onmessage = function (e) {
      const workerResult = e.data;

      that.workerData = workerResult;
      that.createReportResult();
    };

    this.timeWorker.onerror = function (e) {
      globalToast.error(__('costs_calculating_error'));
    };
  }

  calculateTimeByWorker(projectIds, baselineTasks, calculationMode) {
    this.calculationMode = calculationMode;
    this.workerData = {};
    this.tasksWithResourcesWithoutEstimation = {};

    const currentFilter = Store.getters['filter/currentFilter'];
    const filterStartDate = currentFilter?.properties?.rangeDate?.start || null;
    const filterEndDate = currentFilter?.properties?.rangeDate?.end || null;
    const filterRange = filterStartDate && filterEndDate ? { start: moment(filterStartDate), end: moment(filterEndDate) } : null;

    if (projectIds?.length) {
      projectIds.forEach(projectId => {
        const allTasks = Store.getters['tasksModel/getTasksForGantt'](projectId).data;
        const totalEstimateData = allTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
        const projectTasks = allTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
        const filteredTasks = projectTasks.filter(task => reportHelpers.returnTaskByRange(task, filterRange));

        this.prepareTasksAndLogs(projectId, filteredTasks, currentFilter, totalEstimateData, false);
      });

      if (baselineTasks?.length) {
        this.prepareBaselineTasksForWorker(baselineTasks, filterRange, currentFilter);
      }
    }

    this.sendDataToWorker(this.workerData);
  }

  prepareTasksAndLogs(projectId, tasks, currentFilter, totalEstimateData, isBaseline) {
    const tasksForWorker = [];
    const tasksWithResourcesWithoutEstimation = [];

    const filterStartDate = currentFilter?.properties?.rangeDate?.start || null;
    const filterEndDate = currentFilter?.properties?.rangeDate?.end || null;

    const rangeForWorker = {
      start: filterStartDate || totalEstimateData.start_date,
      end: filterEndDate || totalEstimateData.end_date,
    };

    tasks.forEach(taskData => {
      let resourcesOnTask = [];

      if (isBaseline) {
        resourcesOnTask = taskData.resources;
      } else {
        resourcesOnTask = Store.getters['tasksModel/getResourcesForTask'](projectId, taskData.id);
      }

      const sumLogs = this.calculateLogsByTaskId(currentFilter, projectId, taskData.id);

      let sumLogsByAssignedOnTask = 0;
      let allResourcesWithoutEstimations = true;

      if (!resourcesOnTask?.length) {
        tasksForWorker.push({
          ...taskData,
          logs: sumLogs / 60,
        });

        return;
      }

      resourcesOnTask.forEach(res => {
        const resourceData = Store.getters['resourcesModel/getResourceById'](res.resource_id);
        const resourceType = Store.getters['resourcesModel/getResourceTypeOnProject'](res.resource_id, projectId);
        const preparedResourceDataForCalculations = {
          ...res,
          ...resourceData,
        };

        if ([RESOURCE_TYPES.COST, RESOURCE_TYPES.ITEM].includes(resourceType)) {
          tasksWithResourcesWithoutEstimation.push({
            ...taskData,
            resources: [resourceData],
            estimation: 0,
            logs: 0,
          });
        }

        if ([RESOURCE_TYPES.TIME].includes(resourceType)) {
          const assignedLogs = this.calculateLogsByResourceIdAndTaskId(currentFilter, taskData.id, res.resource_id);

          allResourcesWithoutEstimations = false;
          sumLogsByAssignedOnTask += assignedLogs;
          tasksForWorker.push({
            ...taskData,
            resources: [preparedResourceDataForCalculations],
            logs: assignedLogs / 60,
          });
        }
      });

      if (allResourcesWithoutEstimations) {
        tasksForWorker.push({
          ...taskData,
          resources: [],
          logs: sumLogs / 60,
        });
      }

      if (!allResourcesWithoutEstimations && sumLogs > 0 && sumLogs - sumLogsByAssignedOnTask !== 0) {
        tasksWithResourcesWithoutEstimation.push({
          ...taskData,
          resources: [],
          estimation: 0,
          logs: (sumLogs - sumLogsByAssignedOnTask) / 60,
        });
      }
    });

    const key = `${isBaseline ? 'baseline-' : ''}projectId:${projectId}`;

    this.workerData[`${key}`] = { tasks: tasksForWorker, range: rangeForWorker };
    this.tasksWithResourcesWithoutEstimation[`${key}`] = tasksWithResourcesWithoutEstimation;
  }

  prepareBaselineTasksForWorker(baselineTasks, filterRange, currentFilter) {
    const baselineTotalEstimate = baselineTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
    const baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

    const projectId = baselineTotalEstimate.gantt_id;
    const filteredTasks = baselineTasksAndMilestones.filter(task => reportHelpers.returnTaskByRange(task, filterRange));

    this.prepareTasksAndLogs(projectId, filteredTasks, currentFilter, baselineTotalEstimate, true);
  }

  calculateLogsByTaskId(currentFilter, projectId, taskId) {
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const logsData = timeTrackingModel.getLogsByParams(projectId, taskId);

    let sumLogs = 0;

    if (logsData?.logs?.length) {
      const sum = logsData?.logs.filter(log => {
        const logDate = moment(log.date);

        if (startDate && logDate.isBefore(startDate)) {
          return false;
        }

        if (endDate && logDate.isAfter(endDate)) {
          return false;
        }

        return true;
      }).reduce((sum, log) => sum + log.time, 0);

      sumLogs += sum;
    }

    return sumLogs;
  }

  calculateLogsByResourceIdAndTaskId(currentFilter, taskId, resourceId) {
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const logsData = timeTrackingModel.getLogsByResourceId(resourceId);
    const resourceTaskLogs = logsData.filter(item => item.task_id === taskId && item.resource_id === resourceId);

    let sumLogs = 0;

    if (resourceTaskLogs?.length) {
      const sum = resourceTaskLogs.filter(log => {
        const logDate = moment(log.date);

        if (startDate && logDate.isBefore(startDate)) {
          return false;
        }

        if (endDate && logDate.isAfter(endDate)) {
          return false;
        }

        return true;
      }).reduce((sum, log) => sum + log.time, 0);

      sumLogs += sum;
    }

    return sumLogs;
  }

  sendDataToWorker(tasksData) {
    const projectSettingsMap = {};

    projectsModel.serialize().forEach(settings => {
      projectSettingsMap[settings.gantt_id] = {
        ...settings,
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](settings.gantt_id),
      };
    });

    this.timeWorker.postMessage(_.cloneDeep({
      projectConfigs: reportHelpers.prepareProjectConfigs(),
      projectSettings: projectSettingsMap,
      customDaysData: customDayTemplatesModel.serialize(),
      tasksData,
      resourcesData: Store.getters['resourcesModel/getAllResources'],
    }));
  }

  _createTableTask({
    t, baselineTask, projectId, projectData, defaultAssignee,
  }) {
    const resourceId = t.resources[0]?.resource_id || t.resources[0]?.id;
    const assignee = resourceId ? t.resources[0] : defaultAssignee;

    const baselineEstimation = baselineTask?.estimation || 0;
    // const estimation = baselineTask ? 0 : t.estimation;
    const estimation = t?.estimation || 0;

    return {
      id: t.id,
      task: { id: t.id, name: t.text },
      project: { id: projectId, name: projectData.name },
      assignee,
      baseline_estimation: +baselineEstimation,
      estimation,
      logs: 0,
      variance: +baselineEstimation - estimation,
    };
  }

  _findTaskById(tasks, taskId, resourceId) {
    return tasks.find(b => b.id === taskId && (
      resourceId
        ? b.resources[0]?.resource_id === resourceId
        : !b.resources.length
    ));
  }

  _syncTasks({
    currentTasks, baselineTasks, projectId, projectData, defaultAssignee,
  }) {
    const tableTasks = [];

    currentTasks.forEach(t => {
      const resourceId = t.resources[0]?.resource_id || t.resources[0]?.id;
      const baselineTask = this._findTaskById(baselineTasks, t.id, resourceId);

      tableTasks.push(this._createTableTask({
        t,
        baselineTask,
        projectId,
        projectData,
        defaultAssignee,
      }));
    });

    baselineTasks.forEach(t => {
      const resourceId = t.resources[0]?.resource_id;
      const task = this._findTaskById(currentTasks, t.id, resourceId);

      if (!task) {
        tableTasks.push(this._createTableTask({
          t,
          baselineTask: t,
          projectId,
          projectData,
          defaultAssignee,
        }));
      }
    });

    return tableTasks;
  }

  prepareTableTasks() {
    const tableTasks = [];
    const workerData = _.pickBy(this.workerData, (v, k) => !k.startsWith('baseline-'));
    const baselineWorkerData = _.pickBy(this.workerData, (v, k) => k.startsWith('baseline-'));

    if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED) {
      Object.entries(workerData).forEach(([key, tasks]) => {
        const allTasksData = [
          ...tasks,
          ...this.tasksWithResourcesWithoutEstimation[key],
        ];

        const projectId = key.split(':')[1];
        const projectData = projectsModel.getProjectDataById(projectId);

        allTasksData.forEach(task => {
          const taskResource = task.resources[0];
          const variance = task.estimation - task.logs;

          const preparedTask = {
            id: task.id,
            task: { id: task.id, name: task.text },
            project: { id: projectId, name: projectData.name, color: ColorCycler.getColor(projectId) },
            assignee: taskResource || { id: '-1', name: __('notAssigned') },
            baseline_estimation: +task.estimation,
            estimation: +task.estimation,
            logs: task.logs,
            variance,
          };

          tableTasks.push(preparedTask);
        });
      });
    }

    if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
      Object.entries(baselineWorkerData).forEach(([key, tasks]) => {
        const allTasksData = [
          ...tasks,
          ...this.tasksWithResourcesWithoutEstimation[key],
        ];

        const projectId = key.split(':')[1];
        const projectData = projectsModel.getProjectDataById(projectId);

        allTasksData.forEach(task => {
          const taskResource = task.resources[0];
          const variance = task.estimation - task.logs;

          const preparedTask = {
            id: task.id,
            task: { id: task.id, name: task.text },
            project: { id: projectId, name: projectData.name, color: ColorCycler.getColor(projectId) },
            assignee: taskResource || { id: '-1', name: __('notAssigned') },
            baseline_estimation: +task.estimation,
            estimation: +task.estimation,
            logs: task.logs,
            variance,
          };

          tableTasks.push(preparedTask);
        });
      });
    }

    if (this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
      Object.entries(workerData).forEach(([key, tasks]) => {
        const projectId = key.split(':')[1];
        const projectData = projectsModel.getProjectDataById(projectId);

        const tasksWithEstimation = tasks;
        const baselineTasksWithEstimation = baselineWorkerData[`baseline-${key}`];

        const tasksWithResourcesWithoutEstimation = this.tasksWithResourcesWithoutEstimation[key];
        const baselineTasksWithResourcesWithoutEstimation = this.tasksWithResourcesWithoutEstimation[`baseline-${key}`];

        const defaultAssignee = { id: '-1', name: __('notAssigned') };

        tableTasks.push(
          ...this._syncTasks({
            currentTasks: tasksWithEstimation,
            baselineTasks: baselineTasksWithEstimation,
            projectId,
            projectData,
            defaultAssignee,
          }),
          ...this._syncTasks({
            currentTasks: tasksWithResourcesWithoutEstimation,
            baselineTasks: baselineTasksWithResourcesWithoutEstimation,
            projectId,
            projectData,
            defaultAssignee,
          }),
        );
      });
    }

    return tableTasks;
  }

  createReportResult() {
    const tableTasks = this.prepareTableTasks();

    app.trigger('onAfterResourcesEstimationsCalculation', {
      table: tableTasks,
    });
  }
}

const timeByResource = new TimeByResourceReport();

export default timeByResource;
